import React, { useState } from 'react';
import TablesList from './tableList/tableList';
import { Loader } from '@ui-kit/components/loader';
import { ReactComponent as NoTables } from '@base/assets/images/noTables.svg';
import { ReactComponent as FlashIcon } from '@base/assets/images/flash.svg';
import { useHistory } from 'react-router-dom';
import { ITable, ITableRepository } from '@base/features/game/model/interfaces';
import { container } from 'tsyringe';
import { t, useBehaviorSubject } from '@base/utils/helpers';
import MainButton from '@ui-kit/components/mainButton';
import { MAIN_ROUTES } from '@base/features/routing/constants/mainRoutes';
import { IPlayersOnlineRepository } from '@base/features/player/model/interfaces';

const SORTITEMS = {
  TABLE: 'table',
  PLAYER1: 'player1',
  PLAYER2: 'player2',
  RATING1: 'rating1',
  RATING2: 'rating2',
  POINTS: 'points',
  TICKETS: 'tickets',
};

function compareStrings(s1: string, s2: string, desc: boolean): number {
  let str1 = s1.toLowerCase();
  let str2 = s2.toLowerCase();
  if (desc) [str1, str2] = [str2, str1];
  return str1 < str2 ? -1 : str1 === str2 ? 0 : 1;
}

function sortPlayers(seat: number) {
  return (t1: ITable, t2: ITable, desc: boolean): number => {
    const p1 = t1.seats[seat];
    const p2 = t2.seats[seat];
    if (!p1) return -1;
    if (!p2) return 1;
    if (p1.isTicketPlayer() && p2.isTicketPlayer()) {
      return compareStrings(p1.name, p2.name, desc);
    }
    if (p1.isTicketPlayer()) return 1;
    if (p2.isTicketPlayer()) return -1;
    return compareStrings(p1.name, p2.name, desc);
  };
}

function sortRating(seat: number) {
  return (t1: ITable, t2: ITable): number => {
    const p1 = t1.seats[seat];
    const p2 = t2.seats[seat];
    if (!p1) return 1;
    if (!p2) return -1;

    if (!p1 && !p2) {
      if (t1.ladder && !t2.ladder) return -1;
      if (!t1.ladder && t2.ladder) return 1;
      return 0;
    }
    return p1.rating - p2.rating;
  };
}

const sortCallbacks = {
  [SORTITEMS.TABLE]: (t1: ITable, t2: ITable): number => {
    return t1.tid - t2.tid;
  },

  [SORTITEMS.PLAYER1]: sortPlayers(0),

  [SORTITEMS.PLAYER2]: sortPlayers(1),

  [SORTITEMS.RATING1]: sortRating(0),

  [SORTITEMS.RATING2]: sortRating(1),

  [SORTITEMS.POINTS]: (t1: ITable, t2: ITable): number => {
    const point1 = t1.getMatchString();
    const point2 = t2.getMatchString();
    const firstNum1 = parseInt(point1.match('^([0-9]+)')!.join(''));
    const firstNum2 = parseInt(point2.match('^([0-9]+)')!.join(''));

    return firstNum1 - firstNum2;
  },

  [SORTITEMS.TICKETS]: (t1: ITable, t2: ITable): number => {
    if (!t2.cost && !t1.cost) return 0;
    if (!t1.cost && t2.cost) return 1;
    if (t1.cost && !t2.cost) return -1;
    return t1.cost - t2.cost;
  },
};

function sortTables(sortOptions: { column: string; dir: string }, tablesArray: ITable[]): ITable[] {
  if (tablesArray.length < 2) return tablesArray;
  const { column, dir } = sortOptions;

  tablesArray.sort((t1: ITable, t2: ITable): number => {
    if (dir === 'desc') {
      [t1, t2] = [t2, t1];
    }
    return sortCallbacks[column](t1, t2, dir === 'desc');
  });
  return tablesArray;
}

function TableListLayout() {
  const tablesRepository: ITableRepository = container.resolve('tableRepository');
  const playersOnlineRepository: IPlayersOnlineRepository = container.resolve('playersOnlineRepository');
  const loginReady = useBehaviorSubject(playersOnlineRepository.onLoginReady);
  const tables = useBehaviorSubject(tablesRepository.tables);
  const [sortOptions, setSortOptions] = useState({
    column: SORTITEMS.TICKETS,
    dir: 'asc',
  });
  function handlerSortTables(column: string) {
    let dir = sortOptions.dir;
    if (column === sortOptions.column) {
      dir = dir === 'asc' ? 'desc' : 'asc';
    }
    setSortOptions({
      column: column,
      dir: dir,
    });
  }

  const sortedTables = sortTables(sortOptions, tables);
  const sortType = sortOptions.column;
  const sortDirection = sortOptions.dir;

  return (
    <div className="table-list">
      <div className="table-list__header">
        <div className="table-list__header-item" onClick={() => handlerSortTables(SORTITEMS.TABLE)}>
          <div
            className={
              sortType === SORTITEMS.TABLE
                ? `table-list__header-table table-list__header-item-content ${sortDirection}`
                : 'table-list__header-table'
            }
          >
            {t('table')}
          </div>
        </div>
        <div className={'table-list__header-item'} onClick={() => handlerSortTables(SORTITEMS.PLAYER1)}>
          <div
            className={
              sortType === SORTITEMS.PLAYER1
                ? `table-list__header-player1 table-list__header-item-content ${sortDirection}`
                : 'table-list__header-player1'
            }
          >
            {`${t('player')} 1`}
          </div>
        </div>
        <div className="table-list__header-item" onClick={() => handlerSortTables(SORTITEMS.RATING1)}>
          <div
            className={
              sortType === SORTITEMS.RATING1
                ? `table-list__header-rating1 table-list__header-item-content ${sortDirection}`
                : 'table-list__header-rating1'
            }
          >
            {t('rating')}
          </div>
        </div>
        <div className="table-list__header-item" onClick={() => handlerSortTables(SORTITEMS.PLAYER2)}>
          <div
            className={
              sortType === SORTITEMS.PLAYER2
                ? `table-list__header-player2 table-list__header-item-content ${sortDirection}`
                : 'table-list__header-player2'
            }
          >
            {`${t('player')} 2`}
          </div>
        </div>
        <div className="table-list__header-item" onClick={() => handlerSortTables(SORTITEMS.RATING2)}>
          <div
            className={
              sortType === SORTITEMS.RATING2
                ? `table-list__header-rating2 table-list__header-item-content ${sortDirection}`
                : 'table-list__header-rating2'
            }
          >
            {t('rating')}
          </div>
        </div>
        <div className="table-list__header-item" />
        <div className="table-list__header-item" onClick={() => handlerSortTables(SORTITEMS.POINTS)}>
          <div
            className={
              sortType === SORTITEMS.POINTS
                ? `table-list__header-points table-list__header-item-content ${sortDirection}`
                : 'table-list__header-points'
            }
          >
            {t('points')}
          </div>
        </div>
        <div className="table-list__header-item" onClick={() => handlerSortTables(SORTITEMS.TICKETS)}>
          <div
            className={
              sortType === SORTITEMS.TICKETS
                ? `table-list__header-tickets table-list__header-item-content ${sortDirection}`
                : 'table-list__header-tickets'
            }
          >
            {t('tickets')}
          </div>
        </div>
      </div>
      {!loginReady && tables.length > 0 ? (
        <Loader height={150} width={150} />
      ) : (
        <TablesList tables={sortedTables} />
      )}
      {!tables.length && <EmptyLobby />}
    </div>
  );
}

function EmptyLobby() {
  const history = useHistory();
  return (
    <div className="table-list__empty">
      <NoTables />
      <h1>Wait for the tables...</h1>
      <MainButton className="login-btn--submit" onClick={() => history.push(MAIN_ROUTES.game)}>
        <FlashIcon /> {t('create_table')}
      </MainButton>
    </div>
  );
}

export default TableListLayout;
